/**
 * Phase 45 Plan 45-07 — "Pick something up?" card.
 *
 * Renders the unassigned-claimable table inside the Inbox-zero empty
 * state, per UI-SPEC §Pick-something-up section layout (lines 537-545).
 *
 * Header band:
 *   - BoltIcon (ms-orange)
 *   - h2 "Pick something up?" (semantic for screen readers)
 *   - Sub: "{N} unassigned tasks on your projects — claim one to put it on your queue."
 *   - "See all backlog →" link to /projects (RESEARCH Open Q2 fallback)
 *
 * Body:
 *   - Loading  → 3 skeleton rows
 *   - Error    → rose-600 error block + Retry button
 *   - Empty    → "Nothing in the backlog right now. Enjoy the breather."
 *   - Data     → one UnassignedRow per task
 */
import { useMemo, useState } from 'react';
import { BoltIcon } from '@heroicons/react/24/outline';
import { useUnassignedTasks } from '../../hooks/useUnassignedTasks';
import { UnassignedRow, UNASSIGNED_ROW_GRID } from './UnassignedRow';

const COL_LABEL = 'text-[9.5px] uppercase tracking-wider font-semibold text-ms-text-muted';

export function PickSomethingUp() {
  const { data, isLoading, isError, refetch } = useUnassignedTasks();
  // Petra UAT 2026-05-22 — Backlog needs its own project filter so a PM with
  // unassigned work spread across many projects can narrow to one before
  // claiming. Independent of the primary MyTasks filter bar above (which
  // scopes the "tasks assigned to you" list, not the backlog feed).
  const [projectFilter, setProjectFilter] = useState<number | null>(null);
  // Stabilise the array reference so the useMemo hooks below don't invalidate
  // on every render when the query data is still undefined.
  const allTasks = useMemo(() => data ?? [], [data]);
  const projectOptions = useMemo(() => {
    const byId = new Map<number, string>();
    for (const t of allTasks) byId.set(t.projectId, t.projectName);
    return Array.from(byId.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allTasks]);
  const tasks = useMemo(
    () =>
      projectFilter === null
        ? allTasks
        : allTasks.filter((t) => t.projectId === projectFilter),
    [allTasks, projectFilter],
  );

  return (
    <div
      data-testid="pick-something-up"
      // Petra UAT 2026-05-19 — toned down to "secondary" surface so the
      // primary "My tasks" list above keeps visual priority. No border /
      // no card; subtle muted bg + a single divider above. The header
      // shrinks to an eyebrow + caption (was h2 strong + sub-caption).
      className="max-w-3xl mx-auto"
    >
      <div className="flex items-center gap-2 px-4 py-3">
        <BoltIcon className="h-3 w-3 text-ms-text-muted" aria-hidden="true" />
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-ms-text-muted">
            Backlog
          </div>
          <div className="text-[11px] text-ms-text-muted">
            {tasks.length}
            {projectFilter !== null && tasks.length !== allTasks.length
              ? ` of ${allTasks.length}`
              : ''}{' '}
            unassigned tasks on your projects — claim one to put it on your queue.
          </div>
        </div>
        {/* Petra UAT 2026-05-22 — Backlog project filter (independent of the
            page's main project/customer filters above; scopes only the
            unassigned feed). MKS-168 — the "primary" filter bar this
            comment used to reference (MyTasksFilterBar) was unmounted dead
            code and has been removed. */}
        {projectOptions.length > 1 && (
          <label
            className="relative cursor-pointer ml-auto"
            data-testid="pick-something-up-project-filter"
          >
            <select
              aria-label="Filter backlog by project"
              value={projectFilter ?? ''}
              onChange={(e) =>
                setProjectFilter(e.target.value === '' ? null : Number(e.target.value))
              }
              className="text-[11px] py-1 pl-2 pr-7 rounded-md border border-ms-border bg-ms-surface text-ms-text-secondary hover:bg-ms-surface-2 focus:outline-none focus:ring-1 focus:ring-ms-orange/40 appearance-none"
            >
              <option value="">All projects · {projectOptions.length}</option>
              {projectOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <svg
              aria-hidden="true"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 opacity-70 pointer-events-none"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </label>
        )}
      </div>

      <div
        className="grid items-center gap-3 px-4 py-2 border-b border-ms-border-subtle bg-ms-surface-2/40"
        style={{ gridTemplateColumns: UNASSIGNED_ROW_GRID }}
      >
        <span />
        <span className={COL_LABEL}>Code</span>
        <span className={COL_LABEL}>Task</span>
        <span className={COL_LABEL}>Project</span>
        <span className={COL_LABEL}>Priority</span>
        <span className={COL_LABEL}>Size · age</span>
        <span />
      </div>

      {isLoading && (
        <div data-testid="pick-something-up-skeleton">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-[44px] bg-ms-surface-2/40 animate-pulse border-b border-ms-border-subtle"
            />
          ))}
        </div>
      )}

      {isError && (
        <div className="flex items-center gap-3 px-4 py-6 text-[12px] text-rose-600">
          Couldn&apos;t load unassigned tasks. Try again.
          <button
            type="button"
            onClick={() => refetch()}
            className="px-3 py-1.5 text-[11.5px] font-medium border border-ms-orange/40 bg-ms-orange/5 text-ms-orange-dark rounded-md hover:bg-ms-orange/10"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && tasks.length === 0 && (
        <div
          data-testid="pick-something-up-empty"
          className="text-[12px] text-ms-text-muted text-center py-6"
        >
          Nothing in the backlog right now. Enjoy the breather.
        </div>
      )}

      {!isLoading && !isError && tasks.length > 0 && (
        <div>
          {tasks.map((t) => (
            <UnassignedRow key={t.taskId} task={t} />
          ))}
        </div>
      )}
    </div>
  );
}
